import { FULL_COUNTRY_OPTIONS_LIST } from '../../common/constants';
import { RBCPrefix } from './FormStateMapper';

export const DEFAULT_MIN_STRING_LENGTH = 1;
export const GIVEN_NAME_MAX_LENGTH = 40;
export const SURNAME_MAX_LENGTH = 40;
export const EMAIL_MAX_LENGTH = 320;
export const RP_ID_MAX_LENGTH = 36;
export const AB_ID_MAX_LENGTH = 36;
export const COMPANY_NAME_MAX_LENGTH = 35;
export const PHONE_NUMBER_MAX_LENGTH = 16;
export const APT_OR_SUITE_MAX_LENGTH = 25;
export const NUMBER_AND_STREET_NAME_MAX_LENGTH = 255;
export const CITY_MAX_LENGTH = 170;
export const STATE_OR_PROVINCE_MAX_LENGTH = 50;
export const POSTAL_CODE_MAX_LENGTH = 10;
export const PASSPORT_NUMBER_MAX_LENGTH = 9;
export const ISSUING_PLACE_OR_AUTHORITY_MAX_LENGTH = 170;
export const SCHOOL_MAX_LENGTH = 100;
export const POLITICALLY_IMPORTANT_FULL_NAME_MAX_LENGTH = 50;
export const POLITICALLY_IMPORTANT_RELATIONSHIP_MAX_LENGTH = 50;
export const POLITICALLY_IMPORTANT_ROLE_MAX_LENGTH = 50;
export const SOURCE_OF_WEALTH_OTHER_MAX_LENGTH = 90;
export const GIC_FUND_SOURCE_OTHER_MAX_LENGTH = 90;
export const FILE_NAME_MAX_LENGTH = 36;
export const PROVINCE_MAX_LENGTH = 50;
export const MAX_FILE_SIZE_IN_MB_HELPTEXTNUMBER = 5;
export const MAX_FILE_SIZE_FOR_VALIDATION = 5000000; // 5MB
export const FILE_ARR_LENGTH_V2 = 1;
export const STUDENT_ID_MAX_LENGTH = 100;
export const MIN_COUNTRY_COUNT = 1;
export const MAX_COUNTRY_COUNT = 3;
export const OCCUPATION_OTHER_MAX_LENGTH = 50;
export const RBC_EMAIL_REGEX =
  /^([_a-zA-Z0-9-!#$%&'*+-/=?^`{|}~]+(\.[_a-zA-Z0-9-]+)*@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.(([0-9]{1,})|([a-zA-Z]{2,})))?$/;
export const RBC_NUMBER_AND_STREET_REGEX = /^[a-zA-Z0-9 ',-.#+]+$/;

export const SUPPORTED_FILE_TYPES = ['.pdf', '.jpg', '.jpeg', '.png'];
export const SUPPORTED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
export const SUPPORTED_FILES_MIME_TO_EXTENSION_MAP = new Map([
  ['image/jpeg', '.jpeg'],
  ['image/png', '.png'],
  ['application/pdf', '.pdf'],
]);

export const COLUMN_SETTING = 'repeat(13, 1fr)';

// Constants for conditional rendering fields
export const EMPLOYMENT_STATUS_STUDENT_NOT_WORKING = 'Student - not working';
export const EMPLOYMENT_STATUS_STUDENT_WORKING = 'Student - working';
export const EMPLOYMENT_STATUS_EMPLOYED = 'Employed';

// transforms array into string with "and" before the last element
export const SUPPORTED_FILE_TYPES_STR = (
  breakStr = 'and',
  supportedFileTypes = SUPPORTED_FILE_TYPES,
) =>
  supportedFileTypes.reduce(
    (txt, val, i, arr) => txt + (i < arr.length - 1 ? ', ' : ` ${breakStr} `) + val,
  );

export const RBC_GIC_FORM_PROPERTIES = {
  letterOfAcceptanceFile: {
    propertyName: 'letterOfAcceptanceFile' as const,
  },
  prefix: {
    propertyName: 'prefix' as const,
  },
  givenName: {
    propertyName: 'givenName' as const,
  },
  surname: {
    propertyName: 'surname' as const,
  },
  dateOfBirth: {
    propertyName: 'dateOfBirth' as const,
  },
  purpose: {
    propertyName: 'purpose' as const,
  },
  employmentStatus: {
    propertyName: 'employmentStatus' as const,
  },
  occupation: {
    propertyName: 'occupation' as const,
  },
  occupationOther: {
    propertyName: 'occupationOther' as const,
  },
  companyName: {
    propertyName: 'companyName' as const,
  },
  companyPhoneNumber: {
    propertyName: 'companyPhoneNumber' as const,
  },
  levelOfStudy: {
    propertyName: 'levelOfStudy' as const,
  },
  phoneNumber: {
    propertyName: 'phoneNumber' as const,
  },
  isMobileNumber: {
    propertyName: 'isMobileNumber' as const,
  },
  emailAddress: {
    propertyName: 'emailAddress' as const,
  },
  confirmEmailAddress: {
    propertyName: 'confirmEmailAddress' as const,
  },
  aptOrSuiteResidential: {
    propertyName: 'aptOrSuiteResidential' as const,
  },
  numberAndStreetNameResidential: {
    propertyName: 'numberAndStreetNameResidential' as const,
  },
  cityResidential: {
    propertyName: 'cityResidential' as const,
  },
  stateOrProvinceResidential: {
    propertyName: 'stateOrProvinceResidential' as const,
  },
  countryResidential: {
    propertyName: 'countryResidential' as const,
  },
  postalCodeResidential: {
    propertyName: 'postalCodeResidential' as const,
  },
  aptOrSuiteMailing: {
    propertyName: 'aptOrSuiteMailing' as const,
  },
  numberAndStreetNameMailing: {
    propertyName: 'numberAndStreetNameMailing' as const,
  },
  cityMailing: {
    propertyName: 'cityMailing' as const,
  },
  stateOrProvinceMailing: {
    propertyName: 'stateOrProvinceMailing' as const,
  },
  countryMailing: {
    propertyName: 'countryMailing' as const,
  },
  postalCodeMailing: {
    propertyName: 'postalCodeMailing' as const,
  },
  passportNumber: {
    propertyName: 'passportNumber' as const,
  },
  issuingCountry: {
    propertyName: 'issuingCountry' as const,
  },
  issuingPlaceOrAuthority: {
    propertyName: 'issuingPlaceOrAuthority' as const,
  },
  expiryDate: {
    propertyName: 'expiryDate' as const,
  },
  school: {
    propertyName: 'school' as const,
  },
  provinceOfStudy: {
    propertyName: 'provinceOfStudy' as const,
  },
  expectedArrivalDate: {
    propertyName: 'expectedArrivalDate' as const,
  },
  expectedGraduationMonth: {
    propertyName: 'expectedGraduationMonth' as const,
  },
  expectedGraduationYear: {
    propertyName: 'expectedGraduationYear' as const,
  },
  isThirdPartyInvestment: {
    propertyName: 'isThirdPartyInvestment' as const,
  },
  isCanadianResidentForTax: {
    propertyName: 'isCanadianResidentForTax' as const,
  },
  isOtherCountryResidentForTax: {
    propertyName: 'isOtherCountryResidentForTax' as const,
  },
  canadianTaxProvince: {
    propertyName: 'canadianTaxProvince' as const,
  },
  otherCountryResidentForTaxCountry1: {
    propertyName: 'otherCountryResidentForTaxCountry1' as const,
  },
  otherCountryResidentForTaxCountry2: {
    propertyName: 'otherCountryResidentForTaxCountry2' as const,
  },
  otherCountryResidentForTaxCountry3: {
    propertyName: 'otherCountryResidentForTaxCountry3' as const,
  },
  hasPoliticallyImportantPositions: {
    propertyName: 'hasPoliticallyImportantPositions' as const,
  },
  politicallyImportantFullName: {
    propertyName: 'politicallyImportantFullName' as const,
  },
  politicallyImportantRelationship: {
    propertyName: 'politicallyImportantRelationship' as const,
  },
  politicallyImportantRole: {
    propertyName: 'politicallyImportantRole' as const,
  },
  sourceOfWealth: {
    propertyName: 'sourceOfWealth' as const,
  },
  gicFundSource: {
    propertyName: 'gicFundSource' as const,
  },
  sourceOfWealthOther: {
    propertyName: 'sourceOfWealthOther' as const,
  },
  gicFundSourceOther: {
    propertyName: 'gicFundSourceOther' as const,
  },
};

export const PREFIX_LIST = [
  { value: RBCPrefix.Mr, label: RBCPrefix.Mr },
  { value: RBCPrefix.Mrs, label: RBCPrefix.Mrs },
  { value: RBCPrefix.Ms, label: RBCPrefix.Ms },
];

export const VALID_PREFIX_VALUES = PREFIX_LIST.map(option => option.value) as unknown as readonly [
  string,
  ...string[],
];

export const EMPLOYMENT_STATUS_LIST = [
  { value: 'Student - not working', label: 'Student - not working' },
  { value: 'Student - working', label: 'Student - working' },
  { value: 'Employed', label: 'Employed' },
  { value: 'Unemployed', label: 'Unemployed' },
  { value: 'Child', label: 'Child' },
  { value: 'Homemaker', label: 'Homemaker' },
  { value: 'Retired', label: 'Retired' },
  { value: 'Retired - Doctor/Dentist', label: 'Retired - Doctor/Dentist' },
  { value: 'Self-Employed/Business Owner', label: 'Self-Employed/Business Owner' },
  { value: 'Disability Pension', label: 'Disability Pension' },
];

export const VALID_EMPLOYMENT_STATUS_VALUES = EMPLOYMENT_STATUS_LIST.map(
  option => option.value,
) as unknown as readonly [string, ...string[]];

export const STUDENT_WORKING_LIST = [
  {
    value: 'Elementary/Secondary',
    label: 'Student Working - Elementary/Secondary',
  },
  { value: 'Post Secondary', label: 'Student Working - Post Secondary' },
  {
    value: 'Other Post Graduate',
    label: 'Student Working - Other Post Graduate',
  },
  {
    value: 'Graduate Health Sciences',
    label: 'Student Working - Graduate Health Sciences',
  },
  { value: 'Law', label: 'Student Working - Law' },
  { value: 'Dentist', label: 'Student Working - Dentist' },
  { value: 'Doctor', label: 'Student Working - Doctor' },
];

export const STUDENT_WORKING_VALUES = STUDENT_WORKING_LIST.map(
  option => option.value,
) as unknown as readonly [string, ...string[]];

export const STUDENT_NOT_WORKING_LIST = [
  {
    value: 'Elementary/Secondary',
    label: 'Student Not Working - Elementary/Secondary',
  },
  { value: 'Post Secondary', label: 'Student Not Working - Post Secondary' },
  {
    value: 'Other Post Graduate',
    label: 'Student Not Working - Other Post Graduate',
  },
  {
    value: 'Graduate Health Sciences',
    label: 'Student Not Working - Graduate Health Sciences',
  },
  { value: 'Law', label: 'Student Not Working - Law' },
  { value: 'Dentist', label: 'Student Not Working - Dentist' },
  { value: 'Doctor', label: 'Student Not Working - Doctor' },
];

export const STUDENT_NOT_WORKING_VALUES = STUDENT_NOT_WORKING_LIST.map(
  option => option.value,
) as unknown as readonly [string, ...string[]];

export const OTHER_OCCUPATION = 'Other';

export const OCCUPATIONS_LIST: string[] = [
  OTHER_OCCUPATION,
  'Legislators',
  'Deputy Ministers or Equivalent',
  'Governor General, lieutenant governor or heads of government',
  'Political Party Leader/President',
  'Senate of House of Commons Member',
  'Executive Council of Government Member',
  'Ambassador',
  'Attaché',
  'Counsellor of the Ambassador',
  'Head of a government agency',
  'Head of state/government',
  'Mayor',
  'Traditional Chief and Head of Village',
  'Senior Official of Special-Interest Organization',
  'Head of International Organization',
  'President of a state-owned company/bank',
  'President of a Canadian corporation wholly owned by Her Majesty',
  'Managing Directors and Chief Executive - Financial Services',
  'Managing Directors and Chief Executive - Public body',
  'Managing Directors and Chief Executive - Retail',
  'Managing Directors and Chief Executive - Publically Traded company',
  'Managing Directors and Chief Executive - Not for Profit',
  'Managing Directors and Chief Executive - Arts, Entertainment and Recreation',
  'Managing Directors and Chief Executive - Agriculture, Forestry, Fishing and Hunting',
  'Managing Directors and Chief Executive - Administration, Business Support & Waste Management Services',
  'Managing Directors and Chief Executive - Accommodation and Food Services',
  'Managing Directors and Chief Executive - Healthcare and Social Assistance',
  'Managing Directors and Chief Executive - Manufacturing',
  'Managing Directors and Chief Executive - Transportation and Logistics',
  'Finance Manager',
  'Human Resource Manager',
  'Policy and Planning Manager',
  'Business Services and Administration Manager',
  'Sales, Marketing, and Development Manager',
  'Production Manager in Agriculture, Forestry and Fisheries',
  'Manufacturing Manager',
  'Mining Manager',
  'Construction Manager',
  'Supply and Distribution Manager',
  'Information and Communications Technology Services Manager',
  'Child Care Services Manager',
  'Health Services Manager',
  'Aged Care Services Manager',
  'Social Welfare Manager',
  'Education Manager',
  'Financial and Insurance Services Branch Manager',
  'Hotel and Restaurant Manager',
  'Retail and Wholesale Trade Manager',
  'Sports, Recreation and Cultural Centre Manager',
  'Physicists and Astronomer',
  'Meteorologist',
  'Chemist',
  'Geologist and Geophysicist',
  'Mathematician, Actuary and Statistician',
  'Biologist, Botanist and Zoologist Professional',
  'Farming, Forestry and Fisheries Adviser',
  'Environmental Protection Professional',
  'Engineering Professional (excluding electrotechnology)',
  'Electrotechnology engineer',
  'Architect, Planner, Surveyor and Designer',
  'Doctor - Practicing Family Physician',
  'Doctor - Practicing Specialist',
  'Nursing Professional',
  'Midwifery Professional',
  'Traditional and Complementary Medicine',
  'Paramedical Practitioner',
  'Veterinarian',
  'Pharmacist',
  'Environmental and Occupational Health and Hygiene Professional',
  'Physiotherapist',
  'Dietitian and Nutritionist',
  'Audiologist and Speech Therapist',
  'Optometrist and Ophthalmic Optician',
  'Doctor - Non Medical',
  'Dental Hygienist',
  'Dentist - Practicing',
  'Dentist - Resident/Fellow',
  'Doctor - Resident/Fellow',
  'Chiropractor',
  'Massage Therapist',
  'University Professor',
  'Post-Secondary Teacher',
  'Secondary Education Teacher',
  'Primary School Teacher',
  'Early Childhood Educator',
  'Accountant',
  'Financial and Investment Adviser',
  'Financial Analysts',
  'Management and Organization analyst',
  'Policy Administration Professional',
  'Personnel and Careers Professional',
  'Training and Staff Development Professional',
  'Sales, Marketing and Public Relations Professional',
  'Professional Trustee',
  'Information and Communications Technology Sales Professional',
  'Auditor',
  'Appraiser',
  'Project/Program Management',
  'Systems Analyst',
  'Software Developer',
  'Web and Multimedia Developer',
  'Applications Programmer',
  'Database Designers and Administration',
  'Systems Administrator',
  'Computer Network Professional',
  'Lawyer',
  'Judge',
  'Librarian, Archivist and Curator',
  'Economist',
  'Sociologist and Anthropologist',
  'Philosopher, Historian and Political Scientist',
  'Psychologist',
  'Social Work and Counselling Professional',
  'Religious Professional',
  'Author, Journalist and Linguist',
  'Visual Artist',
  'Musician, Singer and Composer',
  'Dancer and Choreographer',
  'Film, Stage Director and Producer',
  'Actor',
  'Announcer on Radio, Television and Other Media',
  'Creative and Performing Artist',
  'Physical and Engineering Science Technician',
  'Mining, Manufacturing and Construction Supervisor',
  'Power Production Plant Operator',
  'Incinerator and Water Treatment Plant Operator',
  'Chemical Processing Plant Controller',
  'Petroleum and Natural Gas Refining Plant Operator',
  'Metal Production Process Controller',
  'Life Science, Agricultural and Forestry Technician',
  'Ship and Aircraft Controller and Technician',
  'Medical and Pharmaceutical Technician',
  'Nursing and Midwifery Associate Professional',
  'Traditional and Complementary Medicine Associate Professional',
  'Veterinary Technician and Assistant',
  'Dental Assistant and Therapist',
  'Community Health Worker',
  'Physiotherapy Technician and Assistant',
  'Medical Assistant',
  'Environmental and Occupational Health Inspector and Associate',
  'Ambulance Worker',
  'Securities and Finance Dealer and Broker',
  'Credit and Loans Officer',
  'Accounting Associate Professional',
  'Statistical and Mathematical Associate Professional',
  'Insurance Representative',
  'Commercial Sales Representative',
  'Buyer',
  'Finance Trade Broker',
  'Conference and Event Planner',
  'Employment Agents and Contractor',
  'Real Estate Agent and Property Manager',
  'Office Supervisor',
  'Legal Secretary',
  'Administrative and Executive Secretary',
  'Medical secretary',
  'Customs and Border Inspector',
  'Government Tax and Excise Official',
  'Government Social Benefits Official',
  'Government Licensing Official',
  'Police Inspectors and Detective',
  'Legal, Social and Religious Associate Professional',
  'Professional Athlete and Sports Player',
  'Sports Coach, Instructor and Official',
  'Fitness and Recreation Instructor and Programmer',
  'Photographer',
  'Interior Designer and Decorator',
  'Gallery, Museum and Library Technician',
  'Chef',
  'Information and Communications Technology Operations and User Support Technician',
  'Telecommunications and Broadcasting Technician',
  'General Office Clerk',
  'Typists and Word Processing operator',
  'Data Entry Clerk',
  'Bank Teller and Clerk',
  'Bookmaker, Croupier and Gaming Worker',
  'Pawnbroker and Money-lender',
  'Debt Collector',
  'Travel Consultant and Clerk',
  'Contact Centre Information Clerk',
  'Telephone Switchboard Operator',
  'Hotel Receptionist',
  'Receptionist',
  'Survey and Market Research Interviewer',
  'Accounting and Bookkeeping Clerk',
  'Statistical, Finance and Insurance Clerk',
  'Payroll Clerk',
  'Stock Clerk',
  'Production Clerk',
  'Transport Clerk',
  'Library Clerk',
  'Mail Carrier and Sorting Clerk',
  'Coding and Proofreading Clerk',
  'Scribe',
  'Filing and Copying Clerk',
  'Personnel Clerk',
  'Travel Attendant, Conductor and Guide',
  'Cook',
  'Waiter',
  'Bartender',
  'Hairdresser, Beautician and Personal Spa Worker',
  'Building and Housekeeping Supervisor',
  'Astrologer and Fortune-teller',
  'Personal Companion and Valet',
  'Undertaker and Embalmer',
  'Pet Groomer and Animal Care Worker',
  'Driving Instructor',
  'Stall and Market Salesperson',
  'Street Food Salesperson',
  'Retail Sales Supervisor',
  'Commercial Sales Supervisor',
  'Retail Shop Sales Assistant',
  'Cashiers and Ticket Clerk',
  'Fashion and Other Model',
  'Sales Demonstrator',
  'Door-to-door Salesperson',
  'Service Station Attendant',
  'Food Service Station Attendant',
  'Food Service Counter Attendant',
  'Automotive Salesperson',
  'Child Care Workers and Teachers’ Aid',
  'Personal Care Worker in Health Services',
  'Firefighter',
  'Police Officer',
  'Prison Guard',
  'Security Guard',
  'Market Gardeners and Crop Grower',
  'Livestock and Dairy Producer',
  'Poultry Producer',
  'Apiarist and Sericulturist',
  'Mixed Crop and Animal Producer',
  'Forestry Worker',
  'Fishery Worker, Hunter, and Trapper',
  'Subsistence Crop Farmer',
  'Subsistence Livestock Farmer',
  'Subsistence Mixed Crop and Livestock Farmer',
  'Subsistence Fisher, Hunter, Trapper, and Gatherer',
  'Building Framer',
  'Building Finisher',
  'Painter, Building Structure Cleaner',
  'Sheet and Structural Metal Worker, Moulder and Welder',
  'Blacksmith and toolmaker',
  'Small and Large Machinery Mechanics and Repair',
  'Handicraft Worker',
  'Printing Trades Worker',
  'Electrical Equipment Installer and Repair',
  'Electronics and Telecommunications Installer and Repair',
  'Butcher and Fishmonger',
  'Baker, Pastry-cook and confectionery Maker',
  'Dairy Products Maker',
  'Tobacco Preparer and Tobacco Products Maker',
  'Cabinet Maker',
  'Woodworking Machine Tool Setter and Operator',
  'Garment Trade Worker',
  'Underwater Diver',
  'Shotfirer and blaster',
  'Product Grade and Tester (excluding Food and Beverage)',
  'Fumigator and other pest and weed controller',
  'Mining and Mineral Processing Plant Operator/Worker',
  'Metal Processing and Finishing Plant Operator',
  'Chemical and Photographic Products Plant and Machine Operator',
  'Rubber, Plastic and Paper Products Machine Operator',
  'Textile, Fur and Leather Products Machine Operator',
  'Food Product and Machine Operator',
  'Wood Processing and Papermaking Plant Operator',
  'Factory Line Assembler',
  'Locomotive Engine Driver',
  'Car, Van and Motorcycle Delivery Courier/Driver',
  'Heavy Truck and Bus Driver',
  'Mobile Plant Operator',
  'Ships’ Deck Crew',
  'Domestic, Hotel and Office Cleaner and Helper',
  'Vehicle, Window, Laundry and Other Hand Cleaning Worker',
  'Agricultural, Forestry and Fishery Labourer',
  'Landscaping and grounds maintenance labourers',
  'Mining and Construction Labourer',
  'Manufacturing Labourer',
  'Transport and Storage Labourer',
  'Plumbing and related labourer',
  'Electrician and related labourer',
  'Gas fitter and related labourer',
  'Millwright',
  'Food Preparation Assistant',
  'Street Services Worker',
  'Street Vendor (excluding food)',
  'Garbage, Recycling and Refuse Worker',
  'Commissioned Armed Forces Officer',
  'Non-commissioned armed forces officer',
  'Armed forces occupations and other ranks',
];

export const GIC_ACCEPTED_COUNTRY_OPTION_LIST = [
  {
    label: 'Brazil',
    value: 'BR',
  },
  {
    label: 'China',
    value: 'CN',
  },
  {
    label: 'Colombia',
    value: 'CO',
  },
  {
    label: 'Costa Rica',
    value: 'CR',
  },
  {
    label: 'India',
    value: 'IN',
  },
  {
    label: 'Peru',
    value: 'PE',
  },
  {
    label: 'Phillipines',
    value: 'PH',
  },
  {
    label: 'Vietnam',
    value: 'VN',
  },
];

export const VALID_GIC_ACCEPTED_COUNTRY_VALUES = GIC_ACCEPTED_COUNTRY_OPTION_LIST.map(
  option => option.value,
) as unknown as readonly [string, ...string[]];

export const PROVINCE_OPTION_LIST = [
  { value: 'CA-AB', label: 'Alberta' },
  { value: 'CA-BC', label: 'British Columbia' },
  { value: 'CA-MB', label: 'Manitoba' },
  { value: 'CA-NB', label: 'New Brunswick' },
  { value: 'CA-NL', label: 'Newfoundland and Labrador' },
  { value: 'CA-NT', label: 'Northwest Territories' },
  { value: 'CA-NS', label: 'Nova Scotia' },
  { value: 'CA-NU', label: 'Nunavut' },
  { value: 'CA-ON', label: 'Ontario' },
  { value: 'CA-PE', label: 'Prince Edward Island' },
  { value: 'CA-QC', label: 'Quebec' },
  { value: 'CA-SK', label: 'Saskatchewan' },
  { value: 'CA-YT', label: 'Yukon' },
];

export const VALID_PROVINCE_VALUES = PROVINCE_OPTION_LIST.map(
  option => option.value,
) as unknown as readonly [string, ...string[]];

export const VALID_FULL_COUNTRY_VALUES = FULL_COUNTRY_OPTIONS_LIST.map(
  option => option.value,
) as unknown as readonly [string, ...string[]];

export const MONTH_OPTION_LIST = [
  { label: 'January', value: '1' },
  { label: 'February', value: '2' },
  { label: 'March', value: '3' },
  { label: 'April', value: '4' },
  { label: 'May', value: '5' },
  { label: 'June', value: '6' },
  { label: 'July', value: '7' },
  { label: 'August', value: '8' },
  { label: 'September', value: '9' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
];

export const VALID_MONTH_VALUES = MONTH_OPTION_LIST.map(
  option => option.value,
) as unknown as readonly [string, ...string[]];

export const POSITIONS_LIST = [
  'Head of state or head of government (i.e. Prime Minister, President)',
  'Member of the legislature (Member of Parliament, Congress, or Senate)',
  'Minister, deputy minister, or equivalent rank',
  'Ambassador, or attaché (Diplomat)',
  'Military General',
  'President or head of a state-owned company or a state-owned bank',
  'Head of a government agency',
  'Judge',
];
export const SOURCE_OF_WEALTH_LIST = [
  'Your own employment',
  'The employment of an immediate family',
  'Social support from the government or from a not for profit organization',
  'Inheritance',
  'Other',
];

export const GIC_FUND_SOURCE_LIST = [
  'Family savings',
  'Borrow from friends or relatives',
  'Sales of investments',
  'Government assistance',
  'Other',
];

export const COUNTRIES_REQUIRE_PROVINCES = ['IN', 'BR', 'CR', 'PE', 'CN', 'PH', 'VN'];
